import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, CalendarClock, HelpCircle, PauseCircle, Undo2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  PageHeader,
  LoadingState,
  KPIGrid,
  MetricCard,
  DataTable,
  TableFilters,
  type Column,
} from '@/components/common';
import { stagger, fadeUp } from '@/lib/animations';
import { rentalsApi, type Rental, type RentalStatus, type RentalSummary } from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { formatCurrency, formatDate } from '@/lib/utils';

/**
 * Equipment we have on hire from somebody else.
 *
 * The hire is arranged and settled in the ERP — purchase order, contract, rate,
 * invoicing — and none of it is reproduced here. The reference on each row is
 * the key back to that record, and the day rate is what the agreement says
 * rather than a figure this platform calculates.
 *
 * What this adds is the half no ERP holds: which job the tool came in for, and
 * whether that job has finished. An ERP knows a rental is open. It does not
 * know the job ended two months ago and the tool has been in a yard since, on
 * rate, doing nothing. That is why idle sorts above overdue — overdue is a
 * conversation with the lessor about a date, idle is money leaving today.
 *
 * The prose the API returns for each row ("Still on hire 61 days after the job
 * it came in for ended") is deliberately not in a cell. In a table the badge
 * and a quantified line say the same thing and scan; the sentence belongs in
 * the alert this will eventually raise.
 */

const STATUS: Record<RentalStatus, { label: string; className: string }> = {
  IDLE: {
    label: 'Job finished',
    className: 'bg-status-destructive-subtle text-status-destructive-foreground',
  },
  OVERDUE: {
    label: 'Overdue',
    className: 'bg-status-destructive-subtle text-status-destructive-foreground',
  },
  OPEN_ENDED: {
    // Dashed rather than muted, like every other unmade decision in this
    // product. An open-ended hire is outstanding, not low priority.
    label: 'No end date',
    // A background as well as a border. Badge's default variant paints
    // bg-primary, so a className that set only the text colour left this
    // rendering as unreadable warning-on-primary — visible in the first
    // screenshot as a solid block where the words should be.
    className:
      'border border-dashed border-status-warning bg-background text-status-warning-foreground',
  },
  DUE_SOON: {
    label: 'Due soon',
    className: 'bg-status-warning-subtle text-status-warning-foreground',
  },
  ON_HIRE: { label: 'On hire', className: 'bg-status-info-subtle text-status-info-foreground' },
  OFF_HIRE: {
    label: 'Returned',
    className: 'bg-status-success-subtle text-status-success-foreground',
  },
};

const STATUS_FILTER_OPTIONS = [
  { value: 'IDLE', label: 'Job finished' },
  { value: 'OVERDUE', label: 'Overdue' },
  { value: 'OPEN_ENDED', label: 'No end date' },
  { value: 'DUE_SOON', label: 'Due soon' },
  { value: 'ON_HIRE', label: 'On hire' },
];

/** The quantified line beneath the badge. Prose is for the alert, not the cell. */
function statusDetail(rental: Rental): string {
  if (rental.daysIdle != null) return `${rental.daysIdle} days idle`;
  if (rental.daysOverdue != null) return `${rental.daysOverdue} days overdue`;
  if (rental.status === 'OPEN_ENDED') return 'no date set';
  if (rental.status === 'DUE_SOON' && rental.expectedOffHireAt) {
    const days = Math.max(
      0,
      Math.ceil((new Date(rental.expectedOffHireAt).getTime() - Date.now()) / 86_400_000)
    );
    return days === 0 ? 'due today' : `due in ${days} days`;
  }
  return '';
}

export default function Rentals() {
  const [rentals, setRentals] = useState<Rental[]>([]);
  const [returned, setReturned] = useState<Rental[]>([]);
  const [summary, setSummary] = useState<RentalSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('attention');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [lessorFilter, setLessorFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const load = () =>
    Promise.all([rentalsApi.getAll(), rentalsApi.getAll(true), rentalsApi.getSummary()])
      .then(([open, all, s]) => {
        setRentals(open);
        setReturned(all.filter((r) => r.status === 'OFF_HIRE'));
        setSummary(s);
        setSelectedIds([]);
      })
      .catch((error) => toast.error(getErrorMessage(error)))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const lessorOptions = useMemo(
    () =>
      [...new Set([...rentals, ...returned].map((r) => r.lessor))]
        .sort()
        .map((l) => ({ value: l, label: l })),
    [rentals, returned]
  );

  const applyFilters = (rows: Rental[]) =>
    rows.filter((r) => {
      const q = search.trim().toLowerCase();
      const matchesSearch =
        !q ||
        r.description.toLowerCase().includes(q) ||
        r.externalReference.toLowerCase().includes(q) ||
        r.lessor.toLowerCase().includes(q) ||
        (r.serialNumber ?? '').toLowerCase().includes(q);
      const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
      const matchesLessor = lessorFilter === 'all' || r.lessor === lessorFilter;
      return matchesSearch && matchesStatus && matchesLessor;
    });

  const recordReturn = async (ids: string[]) => {
    try {
      await Promise.all(ids.map((id) => rentalsApi.recordOffHire(id)));
      toast.success(
        ids.length === 1
          ? 'Rental recorded as returned'
          : `${ids.length} rentals recorded as returned`
      );
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // --- columns -------------------------------------------------------------
  // Two lines per cell where the second is the thing you need alongside the
  // first: the serial with the description, the reference with the lessor.

  const equipmentColumn: Column<Rental> = {
    key: 'description',
    header: 'Equipment',
    sortable: true,
    // The way in. A description names a category — the lessor has several
    // coiled tubing reels — so the row shows the make and the serial that
    // identify this one, and the page carries the rest.
    linkTo: (r) => `/equipment/rentals/${r.id}`,
    render: (r) => (
      <div className="min-w-0">
        <p className="font-medium">{r.description}</p>
        {(r.manufacturer || r.model) && (
          <p className="text-xs text-muted-foreground">
            {[r.manufacturer, r.model].filter(Boolean).join(' ')}
          </p>
        )}
        {r.serialNumber && (
          <p className="font-mono text-xs text-muted-foreground">s/n {r.serialNumber}</p>
        )}
      </div>
    ),
  };

  const lessorColumn: Column<Rental> = {
    key: 'lessor',
    header: 'Lessor & reference',
    sortable: true,
    render: (r) => (
      <div className="min-w-0">
        <p>{r.lessor}</p>
        {/* The key back to the ERP. Whoever chases this settles it there, so
            the reference travels with the name they will call. */}
        <p className="font-mono text-xs text-muted-foreground">{r.externalReference}</p>
        {r.lessorReference && (
          <p className="font-mono text-xs text-muted-foreground">their {r.lessorReference}</p>
        )}
      </div>
    ),
  };

  const jobColumn: Column<Rental> = {
    key: 'jobNumber',
    header: 'Hired for',
    sortable: true,
    render: (r) =>
      r.jobNumber ? (
        <div className="min-w-0">
          <Link
            to={`/jobs/${r.jobId}`}
            className="underline-offset-4 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {r.jobNumber}
          </Link>
          {r.wellName && <p className="text-xs text-muted-foreground">{r.wellName}</p>}
        </div>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  };

  const dayRateColumn: Column<Rental> = {
    key: 'dayRate',
    header: 'Day rate',
    sortable: true,
    className: 'text-right',
    render: (r) =>
      r.dayRate == null ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        <div>
          <p className="font-medium tabular-nums">{formatCurrency(r.dayRate, r.currency)}</p>
          {/* Copied from the agreement, not calculated here. What is owed is
              settled in the ERP. */}
          <p className="text-xs text-muted-foreground">as agreed</p>
        </div>
      ),
  };

  const onHireColumns: Column<Rental>[] = [
    equipmentColumn,
    lessorColumn,
    jobColumn,
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (r) => (
        <div className="min-w-0">
          <Badge className={STATUS[r.status].className}>{STATUS[r.status].label}</Badge>
          {statusDetail(r) && (
            <p className="mt-1 text-xs text-muted-foreground">{statusDetail(r)}</p>
          )}
        </div>
      ),
    },
    {
      key: 'onHireAt',
      header: 'On hire',
      sortable: true,
      render: (r) => (
        <div>
          <p className="tabular-nums">{formatDate(r.onHireAt)}</p>
          <p className="text-xs text-muted-foreground">{r.daysOnHire} days</p>
        </div>
      ),
    },
    {
      key: 'expectedOffHireAt',
      header: 'Due back',
      sortable: true,
      render: (r) =>
        r.expectedOffHireAt ? (
          <span
            className={`tabular-nums ${r.daysOverdue != null ? 'text-status-destructive-foreground' : ''}`}
          >
            {formatDate(r.expectedOffHireAt)}
          </span>
        ) : (
          // Not a dash. A rental nobody dated is the one that reaches 210 days.
          <span className="text-status-warning-foreground">Not set</span>
        ),
    },
    dayRateColumn,
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (r) => (
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            recordReturn([r.id]);
          }}
        >
          <Undo2 className="mr-1.5 h-3.5 w-3.5" />
          Record return
        </Button>
      ),
    },
  ];

  // Status and Due back mean nothing once a rental is back. How long we had it
  // and when it went is what somebody checks before disputing an invoice.
  const returnedColumns: Column<Rental>[] = [
    equipmentColumn,
    lessorColumn,
    jobColumn,
    {
      key: 'onHireAt',
      header: 'On hire',
      sortable: true,
      render: (r) => <span className="tabular-nums">{formatDate(r.onHireAt)}</span>,
    },
    {
      key: 'offHireAt',
      header: 'Returned',
      sortable: true,
      render: (r) => (
        <span className="tabular-nums">{r.offHireAt ? formatDate(r.offHireAt) : '—'}</span>
      ),
    },
    {
      key: 'daysOnHire',
      header: 'Days on hire',
      sortable: true,
      className: 'text-right',
      render: (r) => <span className="tabular-nums">{r.daysOnHire}</span>,
    },
    dayRateColumn,
  ];

  if (loading) return <LoadingState variant="detail" />;

  const attention = applyFilters(rentals.filter((r) => r.needsAction));
  const onHire = applyFilters(rentals);
  const returnedRows = applyFilters(returned);

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        {/* No create action, deliberately: a rental cannot be raised here. */}
        <PageHeader
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Equipment', href: '/equipment' },
            { label: 'Rentals' },
          ]}
          title="Rentals"
          description="Equipment hired from third parties. Hire agreements are raised and settled in the ERP; this page records location and return dates."
        />
      </motion.div>

      {summary && (
        <motion.div variants={fadeUp}>
          <KPIGrid columns={4}>
            <MetricCard
              label="On hire"
              value={summary.onHire}
              icon={<CalendarClock className="h-5 w-5" />}
              sentiment={summary.onHire > 0 ? 'info' : 'success'}
              subtitle="not yet back"
            />
            <MetricCard
              label="Idle — job ended"
              value={summary.idle}
              icon={<PauseCircle className="h-5 w-5" />}
              sentiment={summary.idle > 0 ? 'danger' : 'success'}
              subtitle="on rate for nothing"
            />
            <MetricCard
              label="Overdue"
              value={summary.overdue}
              icon={<AlertTriangle className="h-5 w-5" />}
              sentiment={summary.overdue > 0 ? 'danger' : 'success'}
              subtitle="past the agreed date"
            />
            <MetricCard
              label="No end date"
              value={summary.openEnded}
              icon={<HelpCircle className="h-5 w-5" />}
              sentiment={summary.openEnded > 0 ? 'warning' : 'success'}
              subtitle="nothing will chase them"
            />
          </KPIGrid>
        </motion.div>
      )}

      <motion.div variants={fadeUp}>
        <Tabs
          value={tab}
          onValueChange={(next) => {
            setTab(next);
            setSelectedIds([]);
          }}
          className="w-full"
        >
          <TabsList>
            <TabsTrigger value="attention">Needs attention ({attention.length})</TabsTrigger>
            <TabsTrigger value="onhire">On hire ({onHire.length})</TabsTrigger>
            <TabsTrigger value="returned">Returned ({returnedRows.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="attention" className="mt-6">
            <DataTable<Rental>
              toolbar={
                <TableFilters
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Search equipment, reference or lessor..."
                  filters={[
                    {
                      id: 'status',
                      label: 'Statuses',
                      value: statusFilter,
                      options: STATUS_FILTER_OPTIONS,
                      onChange: setStatusFilter,
                      placeholder: 'Status',
                    },
                    {
                      id: 'lessor',
                      label: 'Lessors',
                      value: lessorFilter,
                      options: lessorOptions,
                      onChange: setLessorFilter,
                      placeholder: 'Lessor',
                    },
                  ]}
                />
              }
              data={attention}
              columns={onHireColumns}
              pagination
              exportable
              exportFilename="rentals-needing-attention"
              columnToggle
              selectable
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              getRowId={(r) => r.id}
              emptyTitle="Nothing needs chasing"
              emptyDescription="Every rental is inside its term with a job still running."
            />
          </TabsContent>

          <TabsContent value="onhire" className="mt-6">
            <DataTable<Rental>
              toolbar={
                <TableFilters
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Search equipment, reference or lessor..."
                  filters={[
                    {
                      id: 'status',
                      label: 'Statuses',
                      value: statusFilter,
                      options: STATUS_FILTER_OPTIONS,
                      onChange: setStatusFilter,
                      placeholder: 'Status',
                    },
                    {
                      id: 'lessor',
                      label: 'Lessors',
                      value: lessorFilter,
                      options: lessorOptions,
                      onChange: setLessorFilter,
                      placeholder: 'Lessor',
                    },
                  ]}
                />
              }
              data={onHire}
              columns={onHireColumns}
              pagination
              exportable
              exportFilename="rentals-on-hire"
              columnToggle
              selectable
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              getRowId={(r) => r.id}
              emptyTitle="Nothing on hire"
              emptyDescription="Rentals are raised in the ERP and appear here so the return is not forgotten."
            />
          </TabsContent>

          <TabsContent value="returned" className="mt-6">
            <DataTable<Rental>
              toolbar={
                <TableFilters
                  search={search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Search equipment, reference or lessor..."
                  filters={[
                    {
                      id: 'status',
                      label: 'Statuses',
                      value: statusFilter,
                      options: STATUS_FILTER_OPTIONS,
                      onChange: setStatusFilter,
                      placeholder: 'Status',
                    },
                    {
                      id: 'lessor',
                      label: 'Lessors',
                      value: lessorFilter,
                      options: lessorOptions,
                      onChange: setLessorFilter,
                      placeholder: 'Lessor',
                    },
                  ]}
                />
              }
              data={returnedRows}
              columns={returnedColumns}
              pagination
              exportable
              exportFilename="rentals-returned"
              columnToggle
              emptyTitle="Nothing returned yet"
              emptyDescription="Rentals appear here once a return is recorded."
            />
          </TabsContent>
        </Tabs>
      </motion.div>

      {/* A yard clerk sending three tools back to one lessor should not click
          three times. */}
      {selectedIds.length > 0 && tab !== 'returned' && (
        <div className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-xl border bg-card p-3 shadow-floating">
          <span className="pl-1 text-sm font-medium">
            {selectedIds.length} rental{selectedIds.length > 1 ? 's' : ''} selected
          </span>
          <Button size="sm" onClick={() => recordReturn(selectedIds)}>
            <Undo2 className="mr-1.5 h-3.5 w-3.5" />
            Record return
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelectedIds([])}>
            Clear
          </Button>
        </div>
      )}
    </motion.main>
  );
}
